import React, { FC, useState, useRef, useEffect } from 'react';
import PlanCard from '../PlanCard/new';
import { Product, SelectedProductsState, Feature, Modals, PlanCardsMetadata } from '~/types/products';
import { Buttons, Card } from '../../types/preamp';
import { Preamp } from '@red-digital/bricks';
import { trackEvent } from '~/helpers/tracking';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, A11y, Virtual, FreeMode } from 'swiper';
import { SwiperOptions } from 'swiper/types';
import 'swiper/swiper-bundle.min.css';
import useMediaQuery from '~/hooks/useMediaQuery';

interface Props {
  metadata?: PlanCardsMetadata;
  activeProducts: Product[];
  addPhoneState: boolean;
  bundleCost: number;
  bundleFeatures: Feature[];
  carousel: {
    direction: number;
    productIndex: number;
    paginate: (index: number) => void;
  };
  sweetener: {
    label: string;
    value: string;
  };
  singleProduct: boolean;
  isCopper: boolean;
  is200: boolean;
  is500: boolean;
  is1Gig: boolean;
  is2Gig: boolean;
  is5Gig: boolean;
  is7Gig: boolean;
  handleSelectPlan?: (plan: Product) => void;
  setActiveModal: (key: Modals) => void;
  setVasModal: (product: Product) => void;
  setEeroModal: (product: Product) => void;
  setBroadbandConsumerLabelModal: (product: Product) => void;
  selectedState?: SelectedProductsState;
  isCopper2Fiber: boolean;
  buttons?: Buttons;
  card?: Card;
  landingPageProduct?: string;
  showPlanCardBanner: boolean;
}

const NewPlanCards: FC<Props> = ({
  activeProducts,
  addPhoneState,
  bundleCost,
  bundleFeatures,
  singleProduct,
  sweetener,
  setActiveModal,
  setVasModal,
  setEeroModal,
  setBroadbandConsumerLabelModal,
  isCopper,
  is200,
  is500,
  is1Gig,
  is2Gig,
  is5Gig,
  is7Gig,
  isCopper2Fiber,
  buttons,
  card,
  handleSelectPlan,
  selectedState,
  landingPageProduct,
  showPlanCardBanner,
}) => {

  useEffect(() => {
    document.body.classList.add('plan-package');

    return () => {
      document.body.classList.remove('plan-package');
    };
  }, []);

  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const { featuredSpeed, stackedOnMobile, categoryTabsEnabled, theme } = card || {};

  /* Equal Height for PlanCard
  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  const [maxLegalHeight, setMaxLegalHeight] = useState(0);
  const maxLegalHeightRef = useRef<number[]>([]);

  const [maxFeatureCollapsedHeight, setMaxFeatureCollapsedHeight] = useState(0);
  const maxFeatureCollapsedHeightRef = useRef<number[]>([]);
  const [maxFeatureExpandedHeight, setMaxFeatureExpandedHeight] = useState(0);
  const maxFeatureExpandedHeightRef = useRef<number[]>([]);

  /* Add filter tabs to product cards
  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  const isTabsEnabled = categoryTabsEnabled === 'true';
  const [activeTab, setActiveTab] = useState('performance');

  const swiperRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isDesktop = useMediaQuery('(min-width: 1440px)');
  const isSmallDesktop = useMediaQuery('(min-width: 1024px) and (max-width: 1440px)');
  const tabFilters = {
    basic: (product) => product.dataspeed.downloadInMbps < 1000,
    performance: (product) => product.dataspeed.downloadInMbps >= 1000 && product.dataspeed.downloadInMbps <= 2000,
    power: (product) => product.dataspeed.downloadInMbps > 2000,
  };
  const productsPerTab = activeProducts.filter(tabFilters[activeTab] || tabFilters.power);

  let filteredActiveProducts = isTabsEnabled && isDesktop ? productsPerTab : activeProducts;
  const has200Mb = filteredActiveProducts?.length > 4;
  const mobileSlideCount = filteredActiveProducts.length;
  const [slideCount, setSlideCount] = useState(mobileSlideCount);

  const handleTabClick = (filterType) => {
    let targetIndex;
    switch (filterType) {
      case 'basic':
        targetIndex = 0;
        break;
      case 'performance':
        if (isSmallDesktop) {
          targetIndex = activeProducts?.length - 5;
        } else targetIndex = activeProducts?.length - 4;
        break;
      case 'power':
        targetIndex = activeProducts?.length - 2;
        break;
      default:
        break;
    }
    swiperRef?.current?.swiper.slideTo(targetIndex);
    setActiveTab(filterType);
  };

  const handleSlideChange = (slide) => {
    const data = {
      elementType: 'SECTION',
      position: `CAROUSEL - SLIDE ${slide}`,
    };
    trackEvent({ action: 'elementViewed', data });

    const { activeIndex } = swiperRef?.current?.swiper || 0;

    // Define index thresholds for tab categories
    const basicToPerformanceIndex = has200Mb && !isSmallDesktop ? 1 : 0;
    const performanceToPowerIndex = isSmallDesktop ? 1 : 3;

    // Determine the new active tab based on the active index
    const newActiveTab = determineActiveTab(activeIndex, basicToPerformanceIndex, performanceToPowerIndex);

    setActiveTab(newActiveTab);
  };

  const determineActiveTab = (activeIndex, basicToPerformanceIndex, performanceToPowerIndex) => {
    if (activeIndex > performanceToPowerIndex) {
      return 'power';
    } else if (activeIndex > basicToPerformanceIndex) {
      return 'performance';
    }
    return 'basic';
  };

  useEffect(() => {
    setActiveTab('performance');
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isMobile) {
        if (stackedOnMobile === 'true' && !isTabsEnabled) swiperRef.current?.swiper.disable();
        swiperRef.current?.swiper.changeDirection(
          stackedOnMobile === 'true' && !isTabsEnabled ? 'vertical' : 'horizontal'
        );
        setSlideCount(mobileSlideCount);
      } else {
        swiperRef.current?.swiper.enable();
        swiperRef.current?.swiper.changeDirection('horizontal');
        setSlideCount(filteredActiveProducts.length);
      }
      swiperRef.current?.swiper.updateSlides();
    };

    handleResize();
  }, [isMobile]);

  return (
    <Preamp placement="4xPj6N8UVVzt18dXyTpo8j">
      {(metadata) => {
        const orderProducts = () => {
          const selectedIndex = filteredActiveProducts.findIndex(
            (product) => product?.productId === landingPageProduct
          );
          // Pre-selected product from landing page always takes precedence
          if (selectedIndex !== -1) {
            filteredActiveProducts.unshift(filteredActiveProducts.splice(selectedIndex, 1)[0])
          } else {
            filteredActiveProducts.sort((x, y) => {
              const xSpeed = x?.dataspeed?.downloadInMbps;
              const ySpeed = y?.dataspeed?.downloadInMbps;
              if (metadata[xSpeed]?.order < metadata[ySpeed]?.order) {
                return -1;
              } else if (metadata[xSpeed]?.order > metadata[ySpeed]?.order) {
                return 1;
              }
              return 0;
            });
          }
        };
        orderProducts();

        const [selectedSpeed] = featuredSpeed?.split('|') ?? [];
        const metadataArray = Object.entries(metadata);
        const featuredCardIndex = metadataArray.findIndex(([key]) => key === selectedSpeed);
        const overflowCarousel = filteredActiveProducts.length > 4 && !isTabsEnabled;
        if (overflowCarousel) {
          filteredActiveProducts = [...filteredActiveProducts, ...filteredActiveProducts, ...filteredActiveProducts];
        }
        const swiperSettings: SwiperOptions = {
          spaceBetween: isTabsEnabled ? 16 : 20,
          autoHeight: false,
          loop: overflowCarousel || false,
          slidesPerView: 'auto',
          centeredSlides: isTabsEnabled && isDesktop ? true : false,
          initialSlide: isTabsEnabled ? 2 : 0,
          roundLengths: true,
          modules: [Virtual, Pagination, Navigation, A11y, FreeMode],
          direction: 'horizontal',
          freeMode: {
            enabled: true,
            sticky: true,
          },
          pagination: {
            enabled: overflowCarousel ? false : true,
            el: '.swiper-pagination',
            clickable: true,
          },
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
            disabledClass: '.swiper-navigation--disabled',
          },
          breakpoints: {
            768: {
              centeredSlides: isTabsEnabled && isDesktop,
              initialSlide: isTabsEnabled ? 2 : overflowCarousel ? 0 : featuredCardIndex,
            },
            1024: {
              centeredSlides: (isTabsEnabled && isDesktop) || overflowCarousel,
              initialSlide: isTabsEnabled ? 1 : overflowCarousel ? 1 : featuredCardIndex,
              pagination: {
                enabled: overflowCarousel ? false : true,
              },
            },
            1224: {
              centeredSlides: (isTabsEnabled && isDesktop) || overflowCarousel,
              initialSlide: isTabsEnabled ? 1 : overflowCarousel ? 1 : featuredCardIndex,
            },
            1280: {
              pagination: { enabled: false },
              initialSlide: isTabsEnabled ? 1 : overflowCarousel ? 2 : featuredCardIndex,
              centeredSlides: (isTabsEnabled && isDesktop) || overflowCarousel,
            },
          },
        };

        return (
          <div className={`${isTabsEnabled ? 'plan-packages__tab-wrapper' : ''}`}>
            {isTabsEnabled && (
              <div className="plan-packages__tabs">
                <button
                  className={`plan-packages__tab ${activeTab === 'basic' ? 'active' : 'inactive'}`}
                  onClick={() => handleTabClick('basic')}
                >
                  Basic
                </button>
                <button
                  className={`plan-packages__tab ${activeTab === 'performance' ? 'active' : 'inactive'}`}
                  onClick={() => handleTabClick('performance')}
                >
                  Performance
                </button>
                <button
                  className={`plan-packages__tab ${activeTab === 'power' ? 'active' : 'inactive'}`}
                  onClick={() => handleTabClick('power')}
                >
                  Power
                </button>
              </div>
            )}
            <div
              className={`
                plan-packages__cards
                w-theme
                ${stackedOnMobile === 'true' && !isTabsEnabled ? 'plan-packages__cards--stacked' : ''}
                ${overflowCarousel && isDesktop ? 'plan-packages__cards--overflow-visible' : ''}
              `}
              data-testid="plan-packages-cards"
            >
              <>
                <div className={`swiper-navigation ${isDesktop && isTabsEnabled ? 'swiper-navigation--disabled' : ''}`}>
                  <div className="swiper-button-prev" />
                  <div className="swiper-pagination" />
                  <div className="swiper-button-next" />
                </div>
                <Swiper
                  ref={swiperRef}
                  className={`plan-card-carousel ${theme || ''}`}
                  onSlideChange={(swiper) => handleSlideChange(swiper.activeIndex + 1)}
                  {...swiperSettings}
                >
                  {filteredActiveProducts.length > 0 &&
                    filteredActiveProducts.map((product, index) => {
                      return (
                        <SwiperSlide key={index} style={{ display: index >= slideCount ? 'none' : '' }}>
                          <PlanCard
                            index={index}
                            metadata={metadata || {}}
                            card={card}
                            selectedCard={selectedCardIndex === index}
                            setSelectedCardIndex={setSelectedCardIndex}
                            addPhoneState={addPhoneState}
                            key={product?.productId}
                            selectedState={selectedState}
                            singleProduct={singleProduct}
                            sweetener={sweetener}
                            product={product}
                            setActiveModal={setActiveModal}
                            setVasModal={setVasModal}
                            bundleCost={bundleCost}
                            bundleFeatures={bundleFeatures}
                            setEeroModal={setEeroModal}
                            setBroadbandConsumerLabelModal={setBroadbandConsumerLabelModal}
                            isCopper={isCopper}
                            is200={is200}
                            is500={is500}
                            is1Gig={is1Gig}
                            is2Gig={is2Gig}
                            is5Gig={is5Gig}
                            is7Gig={is7Gig}
                            isCopper2Fiber={isCopper2Fiber}
                            buttons={buttons}
                            handleSelectPlan={handleSelectPlan}
                            maxLegalHeight={maxLegalHeight}
                            maxLegalHeightRef={maxLegalHeightRef}
                            setMaxLegalHeight={setMaxLegalHeight}
                            maxFeatureCollapsedHeight={maxFeatureCollapsedHeight}
                            maxFeatureCollapsedHeightRef={maxFeatureCollapsedHeightRef}
                            setMaxFeatureCollapsedHeight={setMaxFeatureCollapsedHeight}
                            maxFeatureExpandedHeight={maxFeatureExpandedHeight}
                            maxFeatureExpandedHeightRef={maxFeatureExpandedHeightRef}
                            setMaxFeatureExpandedHeight={setMaxFeatureExpandedHeight}
                            showPlanCardBanner={showPlanCardBanner}
                          />
                        </SwiperSlide>
                      );
                    })}
                </Swiper>
              </>
            </div>
          </div>
        );
      }}
    </Preamp>
  );
};

NewPlanCards.displayName = 'NewPlanCards';

export default NewPlanCards;
